import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Deposit, DepositFilters, GetDepositsResponse } from '../../types';
import apiService from '../../services/api';

interface DepositsState {
  deposits: Deposit[];
  selectedDeposit: Deposit | null;
  statistics: {
    totalAmount: number;
    averageAmount: number;
    totalCount: number;
    statusBreakdown: {
      completed: {
        count: number;
        totalAmount: number;
      };
      pending: {
        count: number;
        totalAmount: number;
      };
      failed: {
        count: number;
        totalAmount: number;
      };
    };
  };
  filters: DepositFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  loading: boolean;
  error: string | null;
}

const initialState: DepositsState = {
  deposits: [],
  selectedDeposit: null,
  statistics: {
    totalAmount: 0,
    averageAmount: 0,
    totalCount: 0,
    statusBreakdown: {
      completed: {
        count: 0,
        totalAmount: 0,
      },
      pending: {
        count: 0,
        totalAmount: 0,
      },
      failed: {
        count: 0,
        totalAmount: 0,
      },
    },
  },
  filters: {},
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
  loading: false,
  error: null,
};

export const fetchDeposits = createAsyncThunk(
  'deposits/fetchDeposits',
  async ({ page, limit, filters }: { page: number; limit: number; filters?: DepositFilters }) => {
    const response = await apiService.getDeposits(page, limit, filters);
    return response;
  }
);

export const fetchDeposit = createAsyncThunk(
  'deposits/fetchDeposit',
  async (depositId: string) => {
    const response = await apiService.getDeposit(depositId);
    return response;
  }
);

export const completeDeposit = createAsyncThunk(
  'deposits/completeDeposit',
  async (
    { depositId, bankTransaction, bankType }: { depositId: string; bankTransaction: string; bankType: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiService.completeDeposit(depositId, { bankTransaction, bankType });
      return { ...response, depositId };
    } catch (error: any) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to verify/complete deposit';
      return rejectWithValue(msg);
    }
  }
);

export const failDeposit = createAsyncThunk(
  'deposits/failDeposit',
  async ({ depositId, reason }: { depositId: string; reason?: string }, { rejectWithValue }) => {
    try {
      const response = await apiService.failDeposit(depositId, reason);
      return { ...response, depositId };
    } catch (error: any) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to reject deposit';
      return rejectWithValue(msg);
    }
  }
);

const depositsSlice = createSlice({
  name: 'deposits',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedDeposit: (state, action: PayloadAction<Deposit | null>) => {
      state.selectedDeposit = action.payload;
    },
    setFilters: (state, action: PayloadAction<DepositFilters>) => {
      state.filters = action.payload;
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.pagination.page = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeposits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeposits.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.deposits = action.payload.data;
          state.pagination = {
            page: action.payload.pagination.page,
            limit: action.payload.pagination.limit,
            total: action.payload.pagination.total,
            pages: action.payload.pagination.pages,
          };
          state.statistics = action.payload.statistics || {
            totalAmount: 0,
            averageAmount: 0,
            totalCount: 0,
            statusBreakdown: {
              completed: { count: 0, totalAmount: 0 },
              pending: { count: 0, totalAmount: 0 },
              failed: { count: 0, totalAmount: 0 },
            },
          };
        }
      })
      .addCase(fetchDeposits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch deposits';
      })
      .addCase(fetchDeposit.fulfilled, (state, action) => {
        state.selectedDeposit = action.payload;
      })
      .addCase(completeDeposit.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.deposits.findIndex(d => d.id === action.payload.depositId);
          if (index !== -1) {
            state.deposits[index] = action.payload.deposit;
          }
        }
      })
      .addCase(failDeposit.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.deposits.findIndex(d => d.id === action.payload.depositId);
          if (index !== -1) {
            state.deposits[index] = action.payload.data;
          }
        }
      });
  },
});

export const { clearError, setSelectedDeposit, setFilters, clearFilters, setPage } = depositsSlice.actions;
export default depositsSlice.reducer; 